import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiShare2, FiCopy, FiMail, FiTwitter, FiLinkedin, FiGlobe, FiEye, FiEyeOff, FiX, FiCheck, FiDownload, FiSave } = FiIcons

// Simple password hashing function (in production, use proper server-side hashing)
const hashPassword = async (password) => {
  if (!password) return null
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(password.trim()) // Trim whitespace
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    console.log('🔒 Password hashed for storage:', { originalLength: password.length, hashLength: hash.length })
    return hash
  } catch (error) {
    console.error('❌ Error hashing password:', error)
    return null
  }
}

const ShareModal = ({ isOpen, onClose, project, onProjectUpdate }) => {
  const [shareSettings, setShareSettings] = useState({
    isPublic: false,
    allowComments: true,
    allowDownload: false,
    password: '',
    expiresAt: null
  })
  const [copied, setCopied] = useState('')
  const [generating, setGenerating] = useState(false)

  // Load existing share settings when modal opens
  React.useEffect(() => {
    if (isOpen && project) {
      setShareSettings({
        isPublic: (!project.is_private && project.status === 'published'), // ensure published state
        allowDownload: project.allow_download || false,
        allowComments: project.allow_comments !== false, // default to true
        password: '', // Don't load password for security
        expiresAt: project.expires_at ? getExpirationOption(project.expires_at) : null
      })
    }
  }, [isOpen, project])

  // Helper function to determine expiration option from date
  const getExpirationOption = (expiresAt) => {
    if (!expiresAt) return null
    
    const expirationDate = new Date(expiresAt)
    const now = new Date()
    const diffMs = expirationDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays <= 1) return '1d'
    if (diffDays <= 7) return '7d'
    if (diffDays <= 30) return '30d'
    if (diffDays <= 90) return '90d'
    return null
  }

  if (!isOpen) return null

  const baseUrl = window.location.origin
  const shareUrl = `${baseUrl}/#/present/${project?.id}`
  const publicUrl = `${baseUrl}/#/public/${project?.id}`

  const handleCopyLink = (url, type) => {
    navigator.clipboard.writeText(url)
    setCopied(type)
    setTimeout(() => setCopied(''), 2000)
  }

  const handleGeneratePublicLink = async () => {
    setGenerating(true)
    
    try {
      const { supabase } = await import('../../lib/supabase')
      
      // Update project to be public (set is_private to false and status to published)
      const { data, error } = await supabase
        .from('projects')
        .update({ 
          is_private: false,
          status: 'published',
          metadata: {
            ...project?.metadata,
            share_settings: {
              ...shareSettings,
              isPublic: true
            }
          }
        })
        .eq('id', project?.id)
        .select() // Add select to return updated data
      
      if (error) {
        console.error('Error making project public:', error)
        alert('Failed to make project public. Please try again.')
      } else {
        setShareSettings(prev => ({ ...prev, isPublic: true }))
        console.log('✅ Project made public successfully')
        
        // Trigger project data refresh in parent component and ensure local state updates
        if (onProjectUpdate && data && data.length > 0) {
          onProjectUpdate(data[0])
        }
        setShareSettings(prev => ({ ...prev, isPublic: true }))
      }
    } catch (error) {
      console.error('Error in handleGeneratePublicLink:', error)
      alert('Failed to make project public. Please try again.')
    }
    
    setGenerating(false)
  }

  const shareToSocial = (platform) => {
    const text = `Check out my presentation: ${project?.title}`
    const url = shareSettings.isPublic ? publicUrl : shareUrl
    
    const socialUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(project?.title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`
    }

    if (socialUrls[platform]) {
      window.open(socialUrls[platform], '_blank', 'width=600,height=400')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Share Presentation</h1>
              <p className="text-sm text-gray-500">Share "{project?.title}" with others</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={async () => {
                try {
                  const { supabase } = await import('../../lib/supabase')
                  
                  // Calculate expiration date
                  let expirationDate = null
                  if (shareSettings.expiresAt) {
                    const now = new Date()
                    switch (shareSettings.expiresAt) {
                      case '1d':
                        expirationDate = new Date(now.getTime() + 24 * 60 * 60 * 1000)
                        break
                      case '7d':
                        expirationDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
                        break
                      case '30d':
                        expirationDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
                        break
                      case '90d':
                        expirationDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)
                        break
                    }
                  }

                  const passwordHash = shareSettings.password ? await hashPassword(shareSettings.password) : null

                  // Update project with share settings
                  const { data, error } = await supabase
                    .from('projects')
                    .update({
                      is_private: !shareSettings.isPublic,
                      status: shareSettings.isPublic ? 'published' : 'draft',
                      expires_at: expirationDate,
                      allow_download: shareSettings.allowDownload,
                      allow_comments: shareSettings.allowComments,
                      password_hash: passwordHash,
                      metadata: {
                        ...project?.metadata,
                        share_settings: shareSettings
                      }
                    })
                    .eq('id', project?.id)
                    .select()

                  if (error) {
                    console.error('Error saving share settings:', error)
                    alert('Failed to save share settings. Please try again.')
                  } else {
                    alert('Share settings saved successfully!')
                    
                    // Trigger project data refresh in parent component
                    if (onProjectUpdate && data && data.length > 0) {
                      onProjectUpdate(data[0])
                    }
                    
                    onClose()
                  }
                } catch (error) {
                  console.error('Error in save settings:', error)
                  alert('Failed to save share settings. Please try again.')
                }
              }}
              disabled={generating}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>{generating ? 'Saving...' : 'Save Settings'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Quick Share Links */}
          <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share Link
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={shareSettings.isPublic ? publicUrl : shareUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
              />
              <button
                onClick={() => handleCopyLink(shareSettings.isPublic ? publicUrl : shareUrl, 'link')}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
              >
                {copied === 'link' ? (
                  <>
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCopy} className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Social Sharing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share to Social Media
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => shareToSocial('twitter')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <SafeIcon icon={FiTwitter} className="w-4 h-4" />
                <span>Twitter</span>
              </button>
              <button
                onClick={() => shareToSocial('linkedin')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
              >
                <SafeIcon icon={FiLinkedin} className="w-4 h-4" />
                <span>LinkedIn</span>
              </button>
              <button
                onClick={() => shareToSocial('email')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <span>Email</span>
              </button>
            </div>
          </div>
        </div>

        {/* Share Settings */}
        <div className="border-t border-gray-200 pt-6 space-y-4">
          <h4 className="font-medium text-gray-900">Share Settings</h4>

          {/* Public Access */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={shareSettings.isPublic ? FiGlobe : FiEye} className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-900">Public Access</p>
                <p className="text-sm text-gray-600">
                  {shareSettings.isPublic 
                    ? 'Anyone with the link can view this presentation'
                    : 'Only people you invite can view'
                  }
                </p>
              </div>
            </div>
            {!shareSettings.isPublic ? (
              <button
                onClick={handleGeneratePublicLink}
                disabled={generating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                {generating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                )}
                <span>{generating ? 'Generating...' : 'Make Public'}</span>
              </button>
            ) : (
              <button
                            onClick={async () => {
              try {
                const { supabase } = await import('../../lib/supabase')
                
                // Update project to be private (set is_private to true)
                const { error } = await supabase
                  .from('projects')
                  .update({ 
                    is_private: true,
                    metadata: {
                      ...project?.metadata,
                      share_settings: {
                        ...shareSettings,
                        isPublic: false
                      }
                    }
                  })
                  .eq('id', project?.id)
                
                if (error) {
                  console.error('Error making project private:', error)
                  alert('Failed to make project private. Please try again.')
                } else {
                  setShareSettings(prev => ({ ...prev, isPublic: false }))
                  console.log('✅ Project made private successfully')
                }
              } catch (error) {
                console.error('Error making project private:', error)
                alert('Failed to make project private. Please try again.')
              }
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Make Private
          </button>
            )}
          </div>

          {/* Allow Comments */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiShare2} className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-900">Allow Comments</p>
                <p className="text-sm text-gray-600">Let viewers leave feedback and suggestions</p>
              </div>
            </div>
            <button
              onClick={() => setShareSettings(prev => ({ ...prev, allowComments: !prev.allowComments }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                shareSettings.allowComments ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  shareSettings.allowComments ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Allow Download */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiDownload} className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-900">Allow Download</p>
                <p className="text-sm text-gray-600">Let viewers download the presentation</p>
              </div>
            </div>
            <button
              onClick={() => setShareSettings(prev => ({ ...prev, allowDownload: !prev.allowDownload }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                shareSettings.allowDownload ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  shareSettings.allowDownload ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Password Protection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password Protection (Optional)
            </label>
            <input
              type="password"
              value={shareSettings.password}
              onChange={(e) => setShareSettings(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Enter password to protect presentation"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            />
          </div>

          {/* Expiration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link Expiration (Optional)
            </label>
            <select
              value={shareSettings.expiresAt || ''}
              onChange={(e) => setShareSettings(prev => ({ ...prev, expiresAt: e.target.value || null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">Never expires</option>
              <option value="1d">1 day</option>
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
            </select>
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShareModal